import React, { memo } from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getIn } from "formik";
import isValid from "date-fns/isValid";

import { InfoTitle } from "../../InfoTitle";
import { ErrorMessage, ContexualHelp } from "../../Notifications";
import { areEqualFieldProps } from "../utils";
import { StyledFormControl, StyledDatePicker } from "./styled";
import { ReactComponent as CalenderIcon } from "../../../assets/icons/calendar.svg";

const DatePickerBase = ({
  field,
  label,
  disabled,
  infoTitle,
  format = "dd/MM/yyyy",
  minDate = new Date(1900, 0, 1),
  maxDate,
  disableFuture,
  form: { errors, touched, setFieldValue },
  datePickerProps = {},
  contextualHelpText,
  views,
  contextualHelpProps = {},
  inputAdornmentPosition = "end",
  onChange
}) => {
  const errorMessage = getIn(errors, field.name);
  const isError = errorMessage && getIn(touched, field.name);

  // Set maxDate if not defined
  maxDate = maxDate === undefined || maxDate ? maxDate : new Date();

  return (
    <ContexualHelp title={contextualHelpText} {...contextualHelpProps}>
      <StyledFormControl className="formControl datePickerWithEndAdorment">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StyledDatePicker
            className={"dateMUIInput"}
            autoOk
            autoComplete="off"
            label={label}
            inputFormat={format}
            onChange={value => {
              if (views?.[0] === "date" && value !== null) {
                value.setHours(0);
                value.setMinutes(0);
              }
              setFieldValue(field.name, value, true);
              if (onChange) {
                onChange(value);
              }
            }}
            disableFuture={disableFuture}
            minDate={minDate || new Date(1900, 0, 1)}
            maxDate={maxDate}
            disabled={disabled}
            views={views || ["year", "month", "day"]}
            value={field.value ? new Date(field.value) : null}
            defaultCalendarMonth={
              field.value && isValid(new Date(field.value)) ? new Date(field.value) : new Date()
            }
            InputAdornmentProps={{
              position: inputAdornmentPosition,
              disablePointerEvents: false
            }}
            PaperProps={{
              classes: {
                root: "MuiDatePicker-paper"
              }
            }}
            components={{
              OpenPickerIcon: CalenderIcon
            }}
            {...datePickerProps}
            renderInput={props => (
              <TextField variant="outlined" {...props} name={field.name} error={isError} />
            )}
          />
        </LocalizationProvider>
        {infoTitle && <InfoTitle title={infoTitle} />}
        {isError && <ErrorMessage error={errorMessage} />}
      </StyledFormControl>
    </ContexualHelp>
  );
};

DatePickerBase.propTypes = {
  field: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  infoTitle: PropTypes.string,
  format: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  disableFuture: PropTypes.bool,
  form: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  inputAdornmentPosition: PropTypes.string,
  datePickerProps: PropTypes.object,
  contextualHelpText: PropTypes.string,
  views: PropTypes.arrayOf(PropTypes.string),
  contextualHelpProps: PropTypes.object,
  onChange: PropTypes.func
};

export const DatePicker = memo(DatePickerBase, areEqualFieldProps);
